import { CommonModule } from "@angular/common";
import { Component, Inject, inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { ApiService } from "./../../../shared/services/api/api.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { environment } from "../../../../environments/environment";

type ResetStep = "email" | "otp" | "password" | "success";

interface ForgotPasswordDialogData {
  email?: string;
}

@Component({
  selector: "app-forgot-password",
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, LoaderComponent],
  templateUrl: "./forgot-password.component.html",
  styleUrl: "./forgot-password.component.scss",
})
export class ForgotPasswordComponent {
  private dialogRef = inject(MatDialogRef<ForgotPasswordComponent>);

  private fb = inject(FormBuilder);

  assetUrl = environment.assetBaseUrl;

  step: ResetStep = "email";

  isLoading = false;
  errorMessage = "";

  resetEmail = "";
  resetToken = "";

  passwordVisible = false;
  confirmPasswordVisible = false;

  forgotPasswordForm: FormGroup;

  otpForm: FormGroup;

  passwordForm: FormGroup;

  constructor(
    private ApiService: ApiService,

    @Inject(MAT_DIALOG_DATA)
    public data: ForgotPasswordDialogData,
  ) {
    this.forgotPasswordForm = this.fb.group({
      email: [data?.email || "", [Validators.required, Validators.email]],
    });

    this.otpForm = this.fb.group({
      otp: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.passwordForm = this.fb.group(
      {
        password: ["", [Validators.required, Validators.minLength(8)]],

        confirmPassword: ["", [Validators.required, Validators.minLength(8)]],
      },
      {
        validators: this.passwordMatchValidator,
      },
    );
  }

  // ==========================================
  // STEP 1
  // Request password reset code
  // ==========================================

  requestResetCode(): void {
    if (this.forgotPasswordForm.invalid || this.isLoading) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    const email = String(this.forgotPasswordForm.get("email")?.value || "")
      .trim()
      .toLowerCase();

    this.isLoading = true;
    this.errorMessage = "";

    this.ApiService.request(
      API_ENDPOINTS.auth.requestPasswordReset,
      "POST",
      {
        email,
      },
      undefined,
      undefined,
      {
        skipAuth: true,
      },
    ).subscribe({
      next: () => {
        this.resetEmail = email;

        this.otpForm.reset();

        this.errorMessage = "";
        this.step = "otp";
      },

      error: (error) => {
        console.error("Password reset request failed:", error);

        this.errorMessage =
          error?.error?.detail ||
          "Unable to send the verification code. Please try again.";
      },

      complete: () => {
        this.isLoading = false;
      },
    });
  }

  // ==========================================
  // STEP 2
  // Verify password reset OTP
  // ==========================================

  verifyResetCode(): void {
    if (this.otpForm.invalid || this.isLoading) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const otp = String(this.otpForm.get("otp")?.value || "").trim();

    this.isLoading = true;
    this.errorMessage = "";

    this.ApiService.request(
      API_ENDPOINTS.auth.verifyPasswordResetOtp,
      "POST",
      {
        email: this.resetEmail,
        otp,
      },
      undefined,
      undefined,
      {
        skipAuth: true,
      },
    ).subscribe({
      next: (response: any) => {
        const token =
          response?.resetToken || response?.reset_token || response?.token;

        if (!token) {
          this.errorMessage = "Unable to continue with the password reset.";

          return;
        }

        this.resetToken = token;

        this.passwordForm.reset();

        this.errorMessage = "";
        this.step = "password";
      },

      error: (error) => {
        console.error("Password reset verification failed:", error);

        this.errorMessage =
          error?.error?.detail ||
          "The verification code is invalid or has expired.";
      },

      complete: () => {
        this.isLoading = false;
      },
    });
  }

  // ==========================================
  // STEP 3
  // Set new password
  // ==========================================

  resetPassword(): void {
    if (this.passwordForm.invalid || this.isLoading) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    if (!this.resetToken) {
      this.errorMessage =
        "Your password reset session has expired. Please start again.";

      return;
    }

    const password = this.passwordForm.get("password")?.value;

    const confirmPassword = this.passwordForm.get("confirmPassword")?.value;

    this.isLoading = true;
    this.errorMessage = "";

    this.ApiService.request(
      API_ENDPOINTS.auth.confirmPasswordReset,
      "POST",
      {
        resetToken: this.resetToken,
        password,
        confirmPassword,
      },
      undefined,
      undefined,
      {
        skipAuth: true,
      },
    ).subscribe({
      next: () => {
        this.resetToken = "";

        this.passwordForm.reset();

        this.errorMessage = "";
        this.step = "success";
      },

      error: (error) => {
        console.error("Password reset failed:", error);

        this.errorMessage =
          error?.error?.detail ||
          "Unable to reset your password. Please try again.";
      },

      complete: () => {
        this.isLoading = false;
      },
    });
  }

  // ==========================================
  // Resend OTP
  // ==========================================

  resendCode(): void {
    if (this.isLoading || !this.resetEmail) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";

    this.ApiService.request(
      API_ENDPOINTS.auth.requestPasswordReset,
      "POST",
      {
        email: this.resetEmail,
      },
      undefined,
      undefined,
      {
        skipAuth: true,
      },
    ).subscribe({
      next: () => {
        this.otpForm.reset();
        this.errorMessage = "";
      },

      error: (error) => {
        console.error("Password reset resend failed:", error);

        this.errorMessage =
          error?.error?.detail || "Unable to resend the verification code.";
      },

      complete: () => {
        this.isLoading = false;
      },
    });
  }

  // ==========================================
  // Navigation
  // ==========================================

  changeEmail(): void {
    this.otpForm.reset();

    this.resetEmail = "";
    this.resetToken = "";

    this.errorMessage = "";
    this.step = "email";
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // ==========================================
  // Password visibility
  // ==========================================

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPasswordVisibility(): void {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  // ==========================================
  // Validator
  // ==========================================

  private passwordMatchValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const password = control.get("password")?.value;

    const confirmPassword = control.get("confirmPassword")?.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword
      ? null
      : {
          passwordMismatch: true,
        };
  }

  // ==========================================
  // Form getters
  // ==========================================

  get email() {
    return this.forgotPasswordForm.get("email");
  }

  get otp() {
    return this.otpForm.get("otp");
  }

  get password() {
    return this.passwordForm.get("password");
  }

  get confirmPassword() {
    return this.passwordForm.get("confirmPassword");
  }
}
